import React, { useState, useEffect } from 'react';
import { Plus, Calendar, Filter, ArrowDown, ArrowUp, Search, Utensils, Brain } from 'lucide-react';
import { QuickMealLogger, MealData } from './QuickMealLogger';
import { MealLogEntry } from './MealLogEntry'; 

interface MealLogWithId extends MealData {
  id: string;
  timestamp: string;
}

export const MealLogDashboard: React.FC = () => {
  const [isLoggerOpen, setIsLoggerOpen] = useState(false);
  const [meals, setMeals] = useState<MealLogWithId[]>([]);
  const [editingMealId, setEditingMealId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  // Load sample data
  useEffect(() => {
    const sampleMeals: MealLogWithId[] = [
      {
        id: '1',
        name: 'Oatmeal with Berries',
        mealType: 'breakfast',
        time: '07:30',
        carbs: 45,
        protein: 8,
        fat: 5,
        calories: 320,
        notes: 'Added cinnamon and a drizzle of honey',
        timestamp: new Date().toISOString()
      },
      {
        id: '2',
        name: 'Chicken Salad',
        mealType: 'lunch',
        time: '12:15',
        carbs: 20,
        protein: 35,
        fat: 15,
        calories: 450,
        timestamp: new Date().toISOString()
      },
      {
        id: '3',
        name: 'Apple with Almond Butter',
        mealType: 'snack',
        time: '15:30',
        carbs: 25,
        protein: 7,
        fat: 16,
        calories: 280,
        timestamp: new Date().toISOString()
      }
    ];
    
    setMeals(sampleMeals);
  }, []);

  const handleSaveMeal = (mealData: MealData) => {
    if (editingMealId) {
      // Update existing meal
      setMeals(prev => prev.map(meal => 
        meal.id === editingMealId 
          ? { ...mealData, id: meal.id, timestamp: meal.timestamp } 
          : meal
      ));
      setEditingMealId(null);
    } else {
      // Add new meal
      const newMeal: MealLogWithId = {
        ...mealData,
        id: Date.now().toString(),
        timestamp: new Date().toISOString()
      };
      setMeals(prev => [...prev, newMeal]);
    }
  };

  const handleEditMeal = (id: string) => {
    const mealToEdit = meals.find(meal => meal.id === id);
    if (mealToEdit) {
      setEditingMealId(id);
      setIsLoggerOpen(true);
    }
  };

  const handleDeleteMeal = (id: string) => {
    setMeals(prev => prev.filter(meal => meal.id !== id));
  };

  // Filter and sort meals
  const filteredMeals = meals
    .filter(meal => 
      (filterType === 'all' || meal.mealType === filterType) &&
      (searchQuery === '' || meal.name.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      // Convert time strings to comparable values (assuming HH:MM format)
      const timeA = a.time.split(':').map(Number);
      const timeB = b.time.split(':').map(Number);
      
      const valueA = timeA[0] * 60 + timeA[1];
      const valueB = timeB[0] * 60 + timeB[1];
      
      return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
    });

  // Calculate daily totals
  const dailyTotals = meals.reduce((acc, meal) => {
    return {
      calories: acc.calories + meal.calories,
      carbs: acc.carbs + meal.carbs,
      protein: acc.protein + meal.protein,
      fat: acc.fat + meal.fat
    };
  }, { calories: 0, carbs: 0, protein: 0, fat: 0 });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center transform rotate-12">
              <Utensils className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              Meal Tracker
            </h2>
          </div>
          <p className="text-sm md:text-base text-slate-600 mt-1">Log and track your meals and snacks with ZippyHealth</p>
        </div>
        <div className="flex items-center space-x-2 md:space-x-3">
          <button
            className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white px-3 py-2 md:px-4 md:py-2 rounded-lg flex items-center space-x-1 md:space-x-2 transition-all transform hover:scale-105"
          >
            <Brain className="h-4 w-4" />
            <span className="text-sm md:text-base">Zippy Food</span>
          </button>
          <button
            onClick={() => {
              setEditingMealId(null);
              setIsLoggerOpen(true);
            }}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-3 py-2 md:px-4 md:py-2 rounded-lg flex items-center space-x-1 md:space-x-2 transition-all transform hover:scale-105"
          >
            <Plus className="h-4 w-4" />
            <span className="text-sm md:text-base">Quick Log</span>
          </button>
        </div>
      </div>

      {/* Daily Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="h-5 w-5 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800">Today's Summary</h3>
          </div>
          <span className="text-sm text-slate-500">{new Date().toLocaleDateString()}</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
            <p className="text-sm text-orange-700 mb-1">Total Calories</p>
            <p className="text-2xl font-bold text-orange-800">{dailyTotals.calories}</p>
          </div>
          <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
            <p className="text-sm text-orange-700 mb-1">Carbs</p>
            <p className="text-2xl font-bold text-orange-800">{dailyTotals.carbs}g</p>
          </div>
          <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
            <p className="text-sm text-orange-700 mb-1">Protein</p>
            <p className="text-2xl font-bold text-orange-800">{dailyTotals.protein}g</p>
          </div>
          <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
            <p className="text-sm text-orange-700 mb-1">Fat</p>
            <p className="text-2xl font-bold text-orange-800">{dailyTotals.fat}g</p>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-200">
          <div className="flex items-center space-x-2">
            <Brain className="h-4 w-4 text-purple-500" />
            <span className="text-sm text-purple-700 font-medium">AI Insight:</span>
            <span className="text-sm text-slate-600">Your carb intake is 15% higher than your daily target. Consider a protein-rich dinner.</span>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0 md:space-x-4">
        <div className="flex items-center space-x-2 w-full md:w-auto">
          <div className="relative">
            <Filter className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="all">All Meals</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snacks</option>
            </select>
          </div>
          
          <button
            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            title={sortOrder === 'asc' ? 'Sort Descending' : 'Sort Ascending'}
          >
            {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
          </button>
        </div>
        
        <div className="relative flex-1 md:max-w-xs w-full">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search meals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Meal List */}
      <div className="space-y-4">
        {filteredMeals.length > 0 ? (
          filteredMeals.map(meal => (
            <MealLogEntry
              key={meal.id}
              meal={meal}
              onEdit={handleEditMeal}
              onDelete={handleDeleteMeal}
            />
          ))
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
            <div className="flex flex-col items-center space-y-2">
              <Utensils className="h-8 w-8 text-slate-400" />
              <p className="text-slate-600 font-medium">No meals found</p>
              {searchQuery && (
                <p className="text-sm text-slate-500">Try adjusting your search or filters</p>
              )}
              {!searchQuery && meals.length === 0 && (
                <button
                  onClick={() => setIsLoggerOpen(true)}
                  className="mt-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-lg flex items-center space-x-2 transition-all"
                >
                  <Plus className="h-4 w-4" />
                  <span>Log your first meal</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Quick Meal Logger Modal */}
      <QuickMealLogger
        isOpen={isLoggerOpen}
        onClose={() => {
          setIsLoggerOpen(false);
          setEditingMealId(null);
        }}
        onSave={handleSaveMeal}
      />
    </div>
  );
};
